import { useMutation } from '@apollo/client';
import type { UpsertRecentUsersMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpsertRecentUsersDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import { useUpsertRecentUsersTRPC } from './useUpsertRecentUsersTRPC';

/**
 * Flag-gated wrapper for the `upsertRecentUsers` mutation. Defaults to the
 * legacy Apollo mutation and delegates to the tRPC variant when the `trpc`
 * feature flag is enabled. Both paths return the same
 * `UpsertRecentUsersMutation` shape so the consumer doesn't branch.
 */
export const useUpsertRecentUsers = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  const [upsertGraphQL, graphqlState] = useMutation(UpsertRecentUsersDocument, {
    update: (cache) => {
      evictField(cache, 'user_search_preferences');
    },
  });

  const trpcMutation = useUpsertRecentUsersTRPC();

  const upsertRecentUsers = async (
    RecentUserIds: string[]
  ): Promise<UpsertRecentUsersMutation> => {
    if (trpcEnabled) {
      return trpcMutation.upsertRecentUsers(RecentUserIds);
    }

    const result = await upsertGraphQL({ variables: { RecentUserIds } });
    if (!result.data) {
      throw new Error('Failed to upsert recent users');
    }

    return result.data;
  };

  if (trpcEnabled) {
    return {
      upsertRecentUsers,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    upsertRecentUsers,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
